// src/components/Dashboard/Sidebar.tsx
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import {
  getBookmarksConfigValueId,
  extractPersonIdFromNotification,
} from '../../components/utils/CorgeeHelper';
import { PlusCircle } from 'lucide-react';
import {
  Home,
  Bookmark,
  Folder,
  Person,
  Delete,
  Notifications,
  Close,
  ArrowDropDown,
  ViewList,
} from '@mui/icons-material';
import type { AlertNotification, AlertSideMenu, SideMenuProject } from '../../types/dashboard';
import { useNavigate, useLocation } from 'react-router-dom';
import { menuService, type SideMenuItem } from '@/services/menuService';
import { dashboardApi, type Dashboard } from '@/services/dashboardApi';
import { useAuth } from '@/contexts/AuthContext';
import { AddCardTypeModal } from '@components/AdminConsole/AddCardTypeModal';
import { useInfoWindow } from '@contexts/InfoWindowContext';
import { useAlertNotifications } from '@contexts/AlertNotificationsContext';
import { NotificationList } from '../NotificationList/NotificationList';
import './Sidebar.css';
import { useVirtualizer } from '@tanstack/react-virtual';
import { getCardBeingDragged } from '@lib/cardDragState';
import { STORAGE_KEYS } from '@lib/constants';
import { dashboardGroupsService } from '@components/DashboardGroups';
import { useToast } from '@components/ui/Toast';
import { useSocketConnectionId } from '@/contexts/SocketContext';
import { conversationService } from '@/services/conversationService';
import { dashboardService } from '@/services/dashboardService';
import DuplicatedItemsModal from '@components/DashboardGroups/modals/DuplicatedItemsModal';
import { removeMovedCardsFromSource } from '@components/DashboardGroups/dashboardCache';

interface SidebarProps {
  isOpen: boolean;
  projects: SideMenuProject[];
  onProjectChange: (projectId: number) => void;
  onRefreshAlerts?: () => void;
  alertSideMenu?: AlertSideMenu[];
  showUpdateProjects?: boolean;
  onRefreshProjects?: () => void;
  onDecrementAlertCount?: (configValueId: number, amount?: number) => void;
}

// Map API icon names to display - we'll use CSS classes from glyphs
const getIconElement = (iconName: string, isActive: boolean = false) =>
{
  const baseClass = `${iconName} text-base`;
  return (
    <span
      className={baseClass}
      style={{ color: isActive ? '#333' : '#6b7280', fontSize: '16px' }}
    ></span>
  );
};

export const Sidebar: React.FC<SidebarProps> = ({
  isOpen,
  projects,
  onProjectChange,
  onRefreshAlerts,
  alertSideMenu = [],
  showUpdateProjects = false,
  onRefreshProjects,
  onDecrementAlertCount,
}) =>
{
  const navigate = useNavigate();
  const location = useLocation();
  const { PersonId, IsAdmin } = useAuth();
  const socketConnectionId = useSocketConnectionId();
  const [showAddCardTypeModal, setShowAddCardTypeModal] = useState(false);
  const { openInfoWindow } = useInfoWindow();
  const { markConversationRead } = useAlertNotifications();

  // isBeta — read from localStorage, react to changes from AdminConsole
  const [isBeta, setIsBeta] = useState(() => localStorage.getItem('C2_isBeta') === 'true');

  useEffect(() =>
  {
    const handler = (e: Event) => setIsBeta((e as CustomEvent<boolean>).detail);
    window.addEventListener('C2_isBeta_changed', handler);
    return () => window.removeEventListener('C2_isBeta_changed', handler);
  }, []);

  // Menu items from API
  const [menuItems, setMenuItems] = useState<SideMenuItem[]>([]);
  const [_loading, setLoading] = useState(true);

  // Notification panel state
  const [openNotifConfigValueId, setOpenNotifConfigValueId] = useState<number | null>(null);
  const [notifPosition, setNotifPosition] = useState<{ left: number; top: number; bellCenterY?: number }>({
    left: 0,
    top: 0,
  });

  const getAlertCount = (configValueId: number): number =>
  {
    const alert = alertSideMenu.find(a => a.ConfigValueId === configValueId);
    return alert ? alert.Total : 0;
  };

  const getTotalAlertCount = (): number =>
  {
    return alertSideMenu.reduce((acc, item) => acc + item.Total, 0);
  };

  // Get active project ID from URL
  const getActiveProjectId = (): number | null =>
  {
    const match = location.pathname.match(/\/ProjectDetails\/(\d+)/);
    return match ? parseInt(match[1]!) : null;
  };

  // Determine active section based on current route
  const getActiveSection = () =>
  {
    if (location.pathname === '/home') return 'home';
    if (location.pathname === '/Bookmarks') return 'bookmarks';
    if (location.pathname === '/RecycleBin') return 'recycle';
    if (location.pathname === '/AddPeople') return 'add-users';
    if (location.pathname === '/Dashboards') return 'dashboards';
    if (location.pathname === '/Dashboards/9') return 'projects';
    if (location.pathname === '/Dashboards/15') return 'users';
    if (location.pathname.startsWith('/ProjectDetails')) return 'none';
    // Check for organize routes
    const organizeMatch = location.pathname.match(/\/Organize\/(\d+)/);
    if (organizeMatch) return `organize-${organizeMatch[1]}`;
    // Check for card type routes
    const match = location.pathname.match(/\/Dashboards\/(\d+)/);
    if (match) return `cardtype-${match[1]}`;
    return 'none';
  };

  const [activeSection, setActiveSection] = useState<string>(getActiveSection());
  const [searchProject, setSearchProject] = useState<string>('');
  const [expandedSections, setExpandedSections] = useState<Record<string, boolean>>(() =>
  {
    const read = (key: string) => localStorage.getItem(key) === 'true';
    return {
      projects: read(STORAGE_KEYS.SIDEBAR_PROJECTS_OPEN),
      cardTypes: read(STORAGE_KEYS.SIDEBAR_CARDTYPES_OPEN),
      organize: read(STORAGE_KEYS.SIDEBAR_ORGANIZE_OPEN) || location.pathname.startsWith('/Organize/'),
    };
  });

  // Organize dashboards state
  const [organizeDashboards, setOrganizeDashboards] = useState<Dashboard[]>([]);
  const [organizeSearch, setOrganizeSearch] = useState<string>('');
  const [organizeLoading, setOrganizeLoading] = useState(false);
  const organizeLoaded = useRef(false);

  // Card drag-to-organize state
  const [isCardDraggingActive, setIsCardDraggingActive] = useState(false);
  const [dragOverDashboardId, setDragOverDashboardId] = useState<number | null>(null);
  const [isCopyMode, setIsCopyMode] = useState(false);
  const { error: toastError } = useToast();

  // Notification banner (shown after move/copy)
  const [dragNotification, setDragNotification] = useState<string | null>(null);
  const dragNotificationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const showDragNotification = useCallback((message: string) =>
  {
    if (dragNotificationTimerRef.current) clearTimeout(dragNotificationTimerRef.current);
    setDragNotification(message);
    dragNotificationTimerRef.current = setTimeout(() => setDragNotification(null), 3000);
  }, []);

  // Pending drag operation for duplicate handling
  interface PendingDragOp {
    targetDashboard: Dashboard;
    conversationId: number;
    sourceGroupId: number;
    sourceDashboardId: number;
    isCopy: boolean;
  }
  const [duplicateModalOpen, setDuplicateModalOpen] = useState(false);
  const pendingDragOpRef = useRef<PendingDragOp | null>(null);

  // Filter projects based on search
  const filteredProjects = useMemo(() =>
  {
    return projects.filter(x => x.Name.toLowerCase().includes(searchProject.toLowerCase()));
  }, [projects, searchProject]);

  // Filter organize dashboards based on search
  const filteredOrganizeDashboards = useMemo(() =>
  {
    return organizeDashboards.filter(d =>
      d.Name.toLowerCase().includes(organizeSearch.toLowerCase()),
    );
  }, [organizeDashboards, organizeSearch]);

  // Virtualizer for projects list
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: filteredProjects.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 32, // Approximate height of a project item
    overscan: 5,
  });

  // Fetch menu items from API
  const fetchMenu = useCallback(async () =>
  {
    setLoading(true);
    try
    {
      const items = await menuService.getMenuByPerson();
      setMenuItems(items);
    }
    catch (error)
    {
      console.error('Error loading menu:', error);
    }
    finally
    {
      setLoading(false);
    }
  }, []);

  useEffect(() =>
  {
    fetchMenu();
  }, [fetchMenu]);

  useEffect(() =>
  {
    window.addEventListener('C2_cardTypes_changed', fetchMenu);
    return () => window.removeEventListener('C2_cardTypes_changed', fetchMenu);
  }, [fetchMenu]);

  // Update active section when location changes
  useEffect(() =>
  {
    setActiveSection(getActiveSection());
    if (location.pathname.startsWith('/Organize/'))
    {
      setExpandedSections(prev => ({ ...prev, organize: true }));
      localStorage.setItem(STORAGE_KEYS.SIDEBAR_ORGANIZE_OPEN, 'true');
    }
  }, [location.pathname]);

  // Fetch organize dashboards when section is first expanded
  useEffect(() =>
  {
    if (expandedSections['organize'] && !organizeLoaded.current)
    {
      const fetchOrganize = async () =>
      {
        setOrganizeLoading(true);
        try
        {
          const dashboards = await dashboardApi.getByConfigValueId(1, undefined, true);
          setOrganizeDashboards(dashboards);
          organizeLoaded.current = true;
        }
        catch (error)
        {
          console.error('Error loading organize dashboards:', error);
        }
        finally
        {
          setOrganizeLoading(false);
        }
      };
      fetchOrganize();
    }
  }, [expandedSections['organize']]);

  // Refresh organize list when a dashboard is renamed
  useEffect(() =>
  {
    const handleRefresh = async () =>
    {
      try
      {
        const dashboards = await dashboardApi.getByConfigValueId(1, undefined, true);
        setOrganizeDashboards(dashboards);
      }
      catch (error)
      {
        console.error('Error refreshing organize dashboards:', error);
      }
    };
    window.addEventListener('organize:refresh', handleRefresh);
    return () => window.removeEventListener('organize:refresh', handleRefresh);
  }, []);

  // Listen for card drag events to enable sidebar drop targets
  useEffect(() =>
  {
    const handleCardDragStart = () =>
    {
      setIsCardDraggingActive(true);
      // Auto-expand organize section so dashboards are visible as drop targets
      setExpandedSections(prev => ({ ...prev, organize: true }));
    };
    const handleCardDragEnd = () =>
    {
      setIsCardDraggingActive(false);
      setDragOverDashboardId(null);
      setIsCopyMode(false);
    };
    window.addEventListener('corgee:card-drag-start', handleCardDragStart);
    window.addEventListener('corgee:card-drag-end', handleCardDragEnd);
    return () =>
    {
      window.removeEventListener('corgee:card-drag-start', handleCardDragStart);
      window.removeEventListener('corgee:card-drag-end', handleCardDragEnd);
    };
  }, []);

  const executeDragToOrganize = useCallback(
    async (op: PendingDragOp, _removeDuplicates: boolean) =>
    {
      try
      {
        const targetGroupId = op.targetDashboard.DashboardGroups?.[0]?.DashboardGroupId ?? 0;
        await conversationService.Adddashboardgroupbyparameters(
          op.targetDashboard.DashboardId,
          op.sourceGroupId,
          op.conversationId,
          targetGroupId,
          true,
          0,
          socketConnectionId || '',
          false,
        );

        if (!op.isCopy && op.sourceGroupId !== 0)
        {
          await dashboardService.deleteDashboardGroupConversation({
            DashboardGroupId: op.sourceGroupId,
            ConversationId: op.conversationId,
            IsFromBookmark: false,
            DashboardGroupIdDefault: 0,
            layersToHide: 0,
            socketConnectionId: socketConnectionId || '',
          });
          removeMovedCardsFromSource(op.sourceDashboardId, [
            { conversationId: op.conversationId, groupId: op.sourceGroupId },
          ]);
        }

        dashboardGroupsService
          .updatePercentDashboardGroupConversations({
            dashboardGroupConvs: [{ DashboardGroupId: op.sourceGroupId }],
            DashboardGroupIdDefault: 0,
          })
          .catch(() => {});

        showDragNotification(op.isCopy ? '1 items copied' : '1 items moved');
      }
      catch (e)
      {
        console.error('Error executing drag-to-organize:', e);
        toastError('Failed to move card to dashboard');
      }
    },
    [socketConnectionId, showDragNotification, toastError],
  );

  const handleDuplicateOk = useCallback(() =>
  {
    setDuplicateModalOpen(false);
    const op = pendingDragOpRef.current;
    if (op) executeDragToOrganize(op, false);
  }, [executeDragToOrganize]);

  const handleDuplicateMoveAll = useCallback(() =>
  {
    setDuplicateModalOpen(false);
    const op = pendingDragOpRef.current;
    if (op) executeDragToOrganize(op, true);
  }, [executeDragToOrganize]);

  const handleDuplicateCancel = useCallback(() =>
  {
    setDuplicateModalOpen(false);
    pendingDragOpRef.current = null;
  }, []);

  const handleDropCardOnOrganizeDashboard = useCallback(
    async (targetDashboard: Dashboard, isCopy: boolean) =>
    {
      const dragged = getCardBeingDragged();
      if (!dragged) return;
      setDragOverDashboardId(null);
      try
      {
        const isDuplicated = await dashboardService.getConversationsDuplicated(
          targetDashboard.DashboardId,
          null,
          String(dragged.conversationId),
        );
        const op: PendingDragOp = {
          targetDashboard,
          conversationId: dragged.conversationId,
          sourceGroupId: dragged.groupId,
          sourceDashboardId: dragged.dashboardId,
          isCopy,
        };
        if (isDuplicated)
        {
          pendingDragOpRef.current = op;
          setDuplicateModalOpen(true);
        }
        else
        {
          executeDragToOrganize(op, false);
        }
      }
      catch (e)
      {
        console.error('Error checking duplicates:', e);
        toastError('Failed to move card to dashboard');
      }
    },
    [executeDragToOrganize, toastError],
  );

  const sectionStorageKey: Record<string, string> = {
    projects: STORAGE_KEYS.SIDEBAR_PROJECTS_OPEN,
    cardTypes: STORAGE_KEYS.SIDEBAR_CARDTYPES_OPEN,
    organize: STORAGE_KEYS.SIDEBAR_ORGANIZE_OPEN,
  };

  const toggleSection = (sectionId: string) =>
  {
    setExpandedSections(prev =>
    {
      const next = !prev[sectionId];
      const key = sectionStorageKey[sectionId];
      if (key)
      {
        localStorage.setItem(key, String(next));
      }
      return { ...prev, [sectionId]: next };
    });
  };

  const handleOpenSidebarNotifications = (configValueId: number, e: React.MouseEvent) =>
  {
    e.stopPropagation();
    if (openNotifConfigValueId === configValueId)
    {
      setOpenNotifConfigValueId(null);
      return;
    }
    const target = e.currentTarget as HTMLElement;
    const rect = target.getBoundingClientRect();
    setNotifPosition({ left: rect.right + 4, top: rect.top, bellCenterY: rect.top + rect.height / 2 });
    setOpenNotifConfigValueId(configValueId);
  };

  const handleSidebarNotificationSelect = (notification: AlertNotification, fromSummary?: boolean) =>
  {
    if (notification.ConversationId)
    {
      markConversationRead(notification.ConversationId);
    }
    if (notification.Type === 'Users')
    {
      const personId = extractPersonIdFromNotification(notification.Body);
      if (personId)
      {
        navigate(`/Person/Authorize/${personId}`);
        return;
      }
    }
    const isPTONotification =
      notification.Type === 'PTO Report - Request Approved' ||
      notification.Type === 'PTO Report - Request Rejected';
    if (isPTONotification)
    {
      if (notification.Body)
      {
        const parser = new DOMParser();
        const doc = parser.parseFromString(notification.Body, 'text/html');
        const linkEl = doc.querySelector('#labela') as HTMLAnchorElement | null;
        const href = linkEl?.getAttribute('href') ?? '';
        const hashIndex = href.indexOf('#/');
        if (hashIndex !== -1)
        {
          navigate(href.slice(hashIndex + 1));
          return;
        }
      }
      if (notification.ConversationId)
      {
        navigate(`/PTOReport/${notification.ConversationId}`);
        return;
      }
    }
    if (notification.ConversationId)
    {
      openInfoWindow(
        notification.ConversationId,
        undefined,
        undefined,
        fromSummary ? { toggle: false } : undefined,
      );
      return;
    }
    // For notifications without a ConversationId, extract the route from the body link
    if (notification.Body)
    {
      const parser = new DOMParser();
      const doc = parser.parseFromString(notification.Body, 'text/html');
      const linkEl = doc.querySelector('#labela') as HTMLAnchorElement | null;
      const href = linkEl?.getAttribute('href') ?? '';
      const hashIndex = href.indexOf('#/');
      if (hashIndex !== -1)
      {
        const route = href.slice(hashIndex + 1); // e.g. "/VacationsApproval"
        navigate(route);
      }
    }
  };

  //if (!isOpen) return null;

  // Get specific items from API
  const dashboardsItem = menuItems.find(item => item.ConfigValueID === 1);
  const engagementsItem = menuItems.find(item => item.ConfigValueID === 9);
  const usersItem = menuItems.find(item => item.ConfigValueID === 15);

  // Card Types are all other items except ConfigValueID 1, 9, 15, and Bookmarks
  const bookmarksId = getBookmarksConfigValueId();
  const cardTypeItems = menuItems
    .filter(item => ![1, 9, 15, bookmarksId].includes(item.ConfigValueID))
    .sort((a, b) => a.OrderSideMenu - b.OrderSideMenu);

  // Resolve the app base path (e.g. '/V2' on QA, '' on localhost)
  // by checking if the first path segment is not a known app route.
  const getAppBasePath = (): string =>
  {
    const browserPath = window.location.pathname;
    const match = browserPath.match(/^(\/[^/]+)(?:\/|$)/);
    if (match && match[1])
    {
      const candidate = match[1];
      const appRoutes = ['/home', '/bookmarks', '/dashboards', '/projectdetails', '/addpeople', '/recyclebin'];
      if (!appRoutes.includes(candidate.toLowerCase())) return candidate;
    }
    return '';
  };

  const handleNavigation = (sectionId: string, path: string, e?: React.MouseEvent | MouseEvent) =>
  {
    if (e && (e.ctrlKey || e.metaKey || e.altKey))
    {
      e.preventDefault();
      window.open(window.location.origin + getAppBasePath() + path, '_blank');
      return;
    }
    setActiveSection(sectionId);
    navigate(path);
  };

  const handleCreateOrganizeDashboard = async (e: React.MouseEvent) =>
  {
    e.stopPropagation();
    try
    {
      await dashboardApi.create({
        DashboardId: 0,
        Name: 'New Dashboard',
        IsPublic: false,
        Archived: false,
        PersonId: PersonId(),
        ConfigValueId: 1,
        ProjectId: null,
        GridSize: 7,
        DashboardViewTypeId: 1,
        Order: organizeDashboards.length,
        FakeDashboardType: 4,
      });
      // Refresh the list
      const dashboards = await dashboardApi.getByConfigValueId(1, undefined, true);
      setOrganizeDashboards(dashboards);
    }
    catch (error)
    {
      console.error('Error creating organize dashboard:', error);
    }
  };

  const renderMenuItem = (
    id: string,
    label: string,
    icon: React.ReactNode,
    options?: {
      badge?: number;
      hasNotification?: boolean;
      configValueId?: number;
      expandable?: boolean;
      hasAddButton?: boolean;
      onAddClick?: (e: React.MouseEvent) => void;
      showSeparator?: boolean;
      onClick?: (e: React.MouseEvent) => void;
      isHeader?: boolean;
      showRefreshButton?: boolean;
      onRefreshClick?: (e: React.MouseEvent) => void;
    },
  ) =>
  {
    const isActive = activeSection === id;
    const {
      badge,
      hasNotification,
      configValueId,
      expandable,
      hasAddButton,
      onAddClick,
      showSeparator,
      onClick,
      isHeader,
      showRefreshButton,
      onRefreshClick,
    } = options || {};

    return (
      <div
        key={id}
        className={showSeparator ? 'border-b border-gray-300 dark:border-[#464949]' : ''}
      >
        <button
          onClick={e =>
          {
            if (expandable)
            {
              toggleSection(id);
            }
            else if (onClick && !isHeader)
            {
              onClick(e);
            }
          }}
          className={`w-full pl-0 pr-2 h-12 flex items-center justify-between transition-colors group ${isActive && !isHeader ? 'text-gray-900 dark:text-gray-100 sidebar-item-active' : 'text-gray-600 dark:text-gray-400'} ${isHeader && !expandable ? 'cursor-default' : 'cursor-pointer'}`}
          style={
            isActive && !isHeader
              ? { backgroundColor: '#fdfdfd', borderRadius: '0 5px 5px 0' }
              : undefined
          }
        >
          <div className="flex items-center min-w-0">
            <div className="w-4 flex-shrink-0 flex items-center justify-center">
              {expandable && (
                <ArrowDropDown
                  style={{
                    fontSize: '18px',
                    color: '#6b7280',
                    transform: expandedSections[id] ? 'rotate(0deg)' : 'rotate(-90deg)',
                    transition: 'transform 0.15s ease',
                    opacity: isOpen ? 1 : 0,
                    transitionProperty: 'transform, opacity',
                  }}
                />
              )}
            </div>
            <span
              className="flex-shrink-0 flex items-center justify-center w-5 sidebar-icon !dark:text-[#aca396]"
              style={{ color: '#666' }}
            >
              {icon}
            </span>
            <span
              className="text-sm sidebar-title !dark:text-[#aca396] overflow-hidden whitespace-nowrap"
              onClick={
                expandable && onClick && !isHeader
                  ? e =>
                  {
                    e.stopPropagation();
                    onClick(e);
                  }
                  : undefined
              }
              style={{ maxWidth: isOpen ? '160px' : '0px', transition: 'max-width 0.3s ease', ...(expandable && onClick ? { cursor: 'pointer' } : {}) }}
            >
              {label}
            </span>
          </div>
          <div className="flex-shrink-0 flex items-center gap-1 overflow-hidden pr-1"
            style={{ maxWidth: isOpen ? '80px' : '0px', opacity: isOpen ? 1 : 0, transition: 'max-width 0.3s ease, opacity 0.2s ease' }}
          >
            {showRefreshButton && (
              <div
                className="p-1 rounded transition-colors cursor-pointer hover:text-[#999]"
                style={{ paddingTop: '12px' }}
                title="Refresh Projects"
                onClick={onRefreshClick}
              >
                <span className="material-icons" style={{ fontSize: '16px', color: '#999' }}>
                  autorenew
                </span>
              </div>
            )}
            {(badge !== undefined && badge > 0) || hasNotification ? (
              <div
                className="p-1 rounded transition-colors relative cursor-pointer"
                title="Notifications"
                onClick={
                  configValueId !== undefined
                    ? e => handleOpenSidebarNotifications(configValueId, e)
                    : undefined
                }
              >
                <Notifications style={{ fontSize: '18px', color: '#9ca3af' }} />
                {badge !== undefined && badge > 0 && (
                  <span className="absolute top-0 -right-1 min-w-[14px] h-3.5 px-1 bg-[#62aacb] dark:bg-[#3c6c80] text-white text-[10px] rounded-full flex items-center justify-center font-bold notification">
                    {badge > 99 ? '99+' : badge}
                  </span>
                )}
              </div>
            ) : null}
            {hasAddButton && (
              <div className="cursor-pointer" onClick={onAddClick}>
                <PlusCircle className="w-4 h-4 text-gray-400" />
              </div>
            )}
          </div>
        </button>
      </div>
    );
  };

  return (
    <aside className={`${isOpen ? 'w-56' : 'w-14'} transition-all duration-300 border-r border-gray-300 dark:border-gray-700 h-full overflow-y-auto overflow-x-hidden flex flex-col bg-[#ededed] dark:bg-[#2d2f2f]`}>
      {/* Menu Items */}
      <nav className="flex-1">
        {/* Fixed: Home — visible only when isBeta is enabled */}
        {isBeta &&
          renderMenuItem(
            'home',
            'Home',
            <Home
              style={{ fontSize: '18px', color: activeSection === 'home' ? '#333' : '#6b7280' }}
            />,
            {
              onClick: () => handleNavigation('home', '/home'),
            },
          )}

        {/* Fixed: Bookmarks */}
        {renderMenuItem(
          'bookmarks',
          'Bookmarks',
          <Bookmark
            style={{ fontSize: '18px', color: activeSection === 'bookmarks' ? '#333' : '#6b7280' }}
          />,
          {
            onClick: e => handleNavigation('bookmarks', '/Bookmarks', e),
          },
        )}

        {/* API: Dashboards (ConfigValueID = 1) */}
        {dashboardsItem &&
          renderMenuItem(
            'dashboards',
            'Dashboards',
            getIconElement(dashboardsItem.Icon, activeSection === 'dashboards'),
            {
              badge: getTotalAlertCount(),
              hasNotification: true,
              configValueId: dashboardsItem.ConfigValueID,
              onClick: e => handleNavigation('dashboards', '/Dashboards', e),
            },
          )}

        {/* API: Projects/Engagements (ConfigValueID = 9) */}
        {engagementsItem &&
          renderMenuItem(
            'projects',
            engagementsItem.Text,
            getIconElement(engagementsItem.Icon, activeSection === 'projects'),
            {
              badge: getAlertCount(engagementsItem.ConfigValueID),
              hasNotification: true,
              configValueId: engagementsItem.ConfigValueID,
              expandable: true,
              onClick: e =>
                handleNavigation('projects', `/Dashboards/${engagementsItem.ConfigValueID}`, e),
              showRefreshButton: showUpdateProjects,
              onRefreshClick: e =>
              {
                e.stopPropagation();
                onRefreshProjects?.();
              },
            },
          )}

        {/* Projects Submenu */}
        {isOpen && expandedSections['projects'] && (
          <div className="border-b border-gray-300 dark:border-[#464949] py-2">
            <div className="relative px-4 mb-2">
              <input
                type="text"
                placeholder="Search a project"
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:border-blue-500 bg-white dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-500 "
                onChange={e => setSearchProject(e.currentTarget.value)}
                value={searchProject}
              />
              {searchProject && (
                <button
                  onClick={() => setSearchProject('')}
                  className="absolute right-5 top-2 -translate-y-1.5 text-gray-400 hover:text-gray-400"
                >
                  <Close className="p-1" />
                </button>
              )}
            </div>

            <div ref={parentRef} className="overflow-y-auto overflow-x-hidden max-h-60">
              <div
                style={{
                  height: `${rowVirtualizer.getTotalSize()}px`,
                  width: '100%',
                  position: 'relative',
                }}
              >
                {rowVirtualizer.getVirtualItems().map(virtualRow =>
                {
                  const project = filteredProjects[virtualRow.index];
                  if (!project) return null;
                  const isActiveProject = getActiveProjectId() === project.ProjectID;
                  return (
                    <div
                      key={virtualRow.key}
                      data-index={virtualRow.index}
                      ref={rowVirtualizer.measureElement}
                      className={`absolute top-0 left-0 w-full px-4 py-2 text-xs cursor-pointer transition-colors ${isActiveProject ? 'text-gray-900 dark:text-gray-100 sidebar-item-active sidebar-subitem-active' : 'hover:underline text-gray-600 dark:text-gray-400 sidebar-subitem'}`}
                      style={{
                        transform: `translateY(${virtualRow.start}px)`,
                        ...(isActiveProject
                          ? { backgroundColor: '#fdfdfd', borderRadius: '0 5px 5px 0' }
                          : {}),
                      }}
                      onClick={e =>
                      {
                        if (e.ctrlKey || e.metaKey || e.altKey)
                        {
                          e.preventDefault();
                          window.open(window.location.origin + getAppBasePath() + `/ProjectDetails/${project.ProjectID}`, '_blank');
                          return;
                        }
                        // Navigation is handled by the host (MainLayout), which flags it
                        // as coming from the side menu so the list is not reordered
                        onProjectChange(project.ProjectID);
                      }}
                    >
                      {project.Name}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}

        {/* API: Users (ConfigValueID = 15) */}
        {usersItem &&
          renderMenuItem(
            'users',
            usersItem.Text,
            <Person
              style={{ fontSize: '18px', color: activeSection === 'users' ? '#333' : '#6b7280' }}
            />,
            {
              badge: getAlertCount(usersItem.ConfigValueID),
              hasNotification: true,
              configValueId: usersItem.ConfigValueID,
              showSeparator: true,
              onClick: e => handleNavigation('users', `/Dashboards/${usersItem.ConfigValueID}`, e),
            },
          )}

        {/* Card Types Header (not selectable) */}
        {renderMenuItem(
          'cardTypes',
          'Card Types',
          <ViewList style={{ fontSize: '18px', color: '#6b7280' }} />,
          {
            expandable: true,
            isHeader: true,
          },
        )}

        {/* Card Types Items (from API) */}
        {isOpen && expandedSections['cardTypes'] &&
          cardTypeItems.map(item =>
          {
            const sectionId = `cardtype-${item.ConfigValueID}`;
            const isActive = activeSection === sectionId;

            return (
              <div key={item.SideMenuId}>
                <button
                  onClick={e => handleNavigation(sectionId, `/Dashboards/${item.ConfigValueID}`, e)}
                  className={`w-full pl-0 pr-2 h-12 flex items-center justify-between transition-colors group ${isActive ? 'text-gray-900 dark:text-gray-100 sidebar-item-active' : 'text-gray-600 dark:text-gray-400'}`}
                  style={
                    isActive
                      ? { backgroundColor: '#fdfdfd', borderRadius: '0 5px 5px 0' }
                      : undefined
                  }
                >
                  <div className="flex items-center min-w-0">
                    <div className="w-4 flex-shrink-0 flex items-center justify-center"></div>
                    <span
                      className="flex-shrink-0 flex items-center justify-center w-5 sidebar-icon"
                      style={{ color: '#666' }}
                    >
                      {getIconElement(item.Icon, isActive)}
                    </span>
                    <span
                      className="text-sm sidebar-title overflow-hidden whitespace-nowrap"
                      style={{ maxWidth: isOpen ? '160px' : '0px', transition: 'max-width 0.3s ease' }}
                    >
                      {item.Text}
                    </span>
                  </div>
                  <div className="flex-shrink-0 flex items-center gap-1 overflow-hidden pr-1 py-1"
                    style={{ maxWidth: isOpen ? '80px' : '0px', transition: 'max-width 0.3s ease' }}
                  >
                    {(() =>
                    {
                      const alertCount = getAlertCount(item.ConfigValueID);
                      return alertCount > 0 ? (
                        <div
                          className="p-1 rounded transition-colors relative cursor-pointer"
                          title="Notifications"
                          onClick={e => handleOpenSidebarNotifications(item.ConfigValueID, e)}
                        >
                          <Notifications style={{ fontSize: '18px', color: '#9ca3af' }} />
                          <span className="absolute -top-0.5 -right-0.5 min-w-[14px] h-3.5 px-1 bg-[#62aacb] text-white text-[10px] rounded-full flex items-center justify-center font-bold notification">
                            {alertCount > 99 ? '99+' : alertCount}
                          </span>
                        </div>
                      ) : (
                        <div
                          className="cursor-pointer"
                          onClick={e => handleOpenSidebarNotifications(item.ConfigValueID, e)}
                        >
                          <Notifications style={{ fontSize: '18px', color: '#9ca3af' }} />
                        </div>
                      );
                    })()}
                  </div>
                </button>
              </div>
            );
          })}

        {/* Fixed: Organize */}
        <div className="border-t border-gray-300 dark:border-gray-700">
          {renderMenuItem(
            'organize',
            'Organize',
            <Folder
              style={{ fontSize: '18px', color: activeSection === 'organize' ? '#333' : '#6b7280' }}
            />,
            {
              expandable: true,
              hasAddButton: true,
              onAddClick: handleCreateOrganizeDashboard,
            },
          )}
        </div>

        {/* Organize Submenu */}
        {isOpen && expandedSections['organize'] && (
          <div className="border-b border-gray-300 dark:border-[#464949] py-2">
            <div className="relative px-4 mb-2">
              <input
                type="text"
                placeholder="Search a dashboard"
                className="w-full px-2 py-1.5 text-xs border border-gray-300 dark:border-gray-600 rounded focus:outline-none focus:border-blue-500 bg-white dark:bg-gray-700 dark:text-gray-100 dark:placeholder-gray-500"
                onChange={e => setOrganizeSearch(e.currentTarget.value)}
                value={organizeSearch}
              />
              {organizeSearch && (
                <button
                  onClick={() => setOrganizeSearch('')}
                  className="absolute right-5 top-2 -translate-y-1.5 text-gray-400 hover:text-gray-400"
                >
                  <Close className="p-1" />
                </button>
              )}
            </div>
            {organizeLoading ? (
              <div className="px-4 py-2 text-xs text-gray-400">Loading...</div>
            ) : (
              filteredOrganizeDashboards.map(dashboard =>
              {
                const isActive = activeSection === `organize-${dashboard.DashboardId}`;
                const isDragTarget =
                  isCardDraggingActive && dragOverDashboardId === dashboard.DashboardId;
                return (
                  <div
                    key={dashboard.DashboardId}
                    className={`px-4 py-2 text-xs cursor-pointer transition-colors ${isActive ? 'text-gray-900 dark:text-gray-100 sidebar-item-active sidebar-subitem-active' : 'hover:underline text-gray-600 dark:text-gray-400 sidebar-subitem'}`}
                    style={{
                      ...(isActive
                        ? { backgroundColor: '#fdfdfd', borderRadius: '0 5px 5px 0' }
                        : undefined),
                      ...(isDragTarget
                        ? {
                          backgroundColor: '#d4edfa',
                          outline: '2px solid #62aacb',
                          borderRadius: '0 5px 5px 0',
                          color: '#1a6a8a',
                        }
                        : undefined),
                    }}
                    onClick={e =>
                      handleNavigation(
                        `organize-${dashboard.DashboardId}`,
                        `/Organize/${dashboard.DashboardId}`,
                        e,
                      )
                    }
                    onDragOver={e =>
                    {
                      if (!getCardBeingDragged()) return;
                      e.preventDefault();
                      // Stop propagation so react-dnd's window-level dragover handler
                      // doesn't override our dropEffect with 'none'
                      e.nativeEvent.stopPropagation();
                      const copy = e.ctrlKey;
                      e.dataTransfer.dropEffect = copy ? 'copy' : 'move';
                      setDragOverDashboardId(dashboard.DashboardId);
                      setIsCopyMode(copy);
                    }}
                    onDragLeave={e =>
                    {
                      if (e.currentTarget.contains(e.relatedTarget as Node)) return;
                      setDragOverDashboardId(null);
                    }}
                    onDrop={e =>
                    {
                      e.preventDefault();
                      handleDropCardOnOrganizeDashboard(dashboard, e.ctrlKey);
                    }}
                  >
                    {dashboard.Name}
                    {isDragTarget && (
                      <span
                        className="ml-2 text-[10px] font-medium"
                        style={{ color: isCopyMode ? '#2563eb' : '#059669' }}
                      >
                        {isCopyMode ? '+ copy' : '→ move'}
                      </span>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}

        {/* Fixed: Add Users (admin only) */}
        {IsAdmin() &&
          renderMenuItem(
            'add-users',
            'Add Users',
            <span
              className="material-icons"
              style={{
                fontSize: '18px',
                color: activeSection === 'add-users' ? '#333' : '#6b7280',
              }}
            >
              person_add
            </span>,
            {
              onClick: e => handleNavigation('add-users', '/AddPeople', e),
            },
          )}

        {/* Fixed: Add Card Type (admin only) */}
        {IsAdmin() &&
          renderMenuItem(
            'add-card-type',
            'Add Card Type',
            <span className="material-icons" style={{ fontSize: '18px', color: '#6b7280' }}>
              add_box
            </span>,
            {
              onClick: () => setShowAddCardTypeModal(true),
            },
          )}

        {/* Fixed: Recycle Bin */}
        {renderMenuItem(
          'recycle',
          'Recycle Bin',
          <Delete
            style={{ fontSize: '18px', color: activeSection === 'recycle' ? '#333' : '#6b7280' }}
          />,
          {
            onClick: e => handleNavigation('recycle', '/RecycleBin', e),
          },
        )}

        {/* Add Card Type modal */}
        {showAddCardTypeModal && (
          <AddCardTypeModal
            onClose={() => setShowAddCardTypeModal(false)}
            onSuccess={() => setShowAddCardTypeModal(false)}
          />
        )}
      </nav>

      {/* Footer */}
      {isOpen && (
        <div className="p-3 border-t border-gray-300 dark:border-[#464949]">
          <p className="text-xs text-gray-500 font-bold text-center sidebar-footer corgee_text">
            Powered by Corgee
          </p>
        </div>
      )}

      {/* Sidebar Notification Panel */}
      {openNotifConfigValueId !== null && (
        <NotificationList
          configValueId={openNotifConfigValueId === 1 ? 0 : openNotifConfigValueId}
          position={notifPosition}
          onClose={() => setOpenNotifConfigValueId(null)}
          onSelectNotification={handleSidebarNotificationSelect}
          onRefreshAlerts={onRefreshAlerts}
          onDecrementAlertCount={onDecrementAlertCount}
        />
      )}

      {/* Drag-to-organize notification banner */}
      {dragNotification && (
        <div
          style={{
            position: 'fixed',
            top: '70px',
            left: '50%',
            transform: 'translateX(-50%)',
            zIndex: 9999,
            background: '#62aacb',
            color: '#fff',
            borderRadius: '8px',
            boxShadow: '0 3px 6px rgba(0,0,0,.2)',
            padding: '6px 20px',
            fontSize: '14px',
            pointerEvents: 'none',
            whiteSpace: 'nowrap',
          }}
        >
          {dragNotification}
        </div>
      )}

      {/* Duplicated items modal */}
      <DuplicatedItemsModal
        open={duplicateModalOpen}
        isCopy={pendingDragOpRef.current?.isCopy}
        onCancel={handleDuplicateCancel}
        onMoveAll={handleDuplicateMoveAll}
        onOk={handleDuplicateOk}
      />
    </aside>
  );
};
